import { ThemeOptions, createTheme } from "@mui/material";
import { mui } from "./brand";
import { palette } from "../styles/design-tokens";

// MUI's palette needs a real colour, so its `primary` is the build's brand hex. A
// partner's brand reaches the app at runtime as --primary-main (partnerBranding),
// so every primary-coloured MUI state reads that var; with no partner it holds the
// same hex, so nothing changes.
const BRAND = `var(--primary-main, ${mui.primary})`;
const BRAND_A = (a: number) => `rgba(var(--primary-main-rgb), ${a})`;
// Theme Studio selection.color (ticks, radios, tab indicators) and input.focus: set
// only for a non-default choice, so each falls back to the brand as shipped.
const SELECTION = `var(--zv-selection, ${BRAND})`;
const INPUT_FOCUS = `var(--zv-input-focus, ${BRAND})`;

export const muiThemeOptions: ThemeOptions = {
  button: {
    main: palette["red-58"],
  },
  typography: {
    // The var() lets a partner's Theme Studio font reach MUI text too.
    fontFamily: `var(--brand-font-body, ${mui.fontBody})`,
    // Headings take the partner's Headings font (set only for a themed partner).
    ...Object.fromEntries(
      (["h1", "h2", "h3", "h4", "h5", "h6"] as const).map((h) => [h, { fontFamily: `var(--zv-font-heading, var(--brand-font-body, ${mui.fontBody}))` }])
    ),
  },
  // Theme Studio corners for MUI's own controls; each fallback is MUI's default.
  components: {
    MuiDialog: { styleOverrides: { paper: { borderRadius: "var(--zv-dialog-radius, 4px)" } } },
    // Menus/popovers follow Theme Studio's "Dropdown menus" corners first, then the dialog corners.
    MuiPopover: { styleOverrides: { paper: { borderRadius: "var(--zv-menu-radius, var(--zv-dialog-radius, 4px))" } } },
    MuiMenu: { styleOverrides: { paper: { borderRadius: "var(--zv-menu-radius, var(--zv-dialog-radius, 4px))" } } },
    // Theme Studio app colours for MUI's own surfaces and controls. Each fallback
    // is MUI's light-palette default, so an unthemed app is unchanged; a
    // component's own sx colour still wins (sx applies after styleOverrides).
    MuiPaper: {
      styleOverrides: {
        root: { backgroundColor: `var(--zv-surface, ${palette["White"]})`, color: `var(--zv-text-primary, ${palette["black-a87"]})` },
      },
    },
    MuiInputBase: { styleOverrides: { root: { color: `var(--zv-text-primary, ${palette["black-a87"]})` } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "var(--zv-input-radius, 4px)",
          backgroundColor: "var(--zv-input-bg, transparent)",
          "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": { borderColor: INPUT_FOCUS },
        },
        notchedOutline: { borderColor: `var(--zv-border, ${palette["black-a23"]})` },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { color: `var(--zv-text-secondary, ${palette["black-a60"]})`, "&.Mui-focused:not(.Mui-error)": { color: INPUT_FOCUS } },
      },
    },
    MuiFormHelperText: { styleOverrides: { root: { color: `var(--zv-text-secondary, ${palette["black-a60"]})` } } },
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState }) => (ownerState.color && ownerState.color !== "default" ? {} : { color: `var(--zv-icon, ${palette["black-a54"]})` }),
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: `var(--zv-icon, ${palette["black-a60"]})`, "&.Mui-checked:not(.Mui-disabled)": { color: SELECTION } } },
    },
    MuiRadio: {
      styleOverrides: { root: { color: `var(--zv-icon, ${palette["black-a60"]})`, "&.Mui-checked:not(.Mui-disabled)": { color: SELECTION } } },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const radius = { borderRadius: "var(--zv-btn-radius-mui, 4px)" };
          if (ownerState.color && ownerState.color !== "primary") return radius;
          if (ownerState.variant === "contained") {
            // MUI's primary.dark is darken(main, 0.3) = main at 70% with black.
            return {
              ...radius,
              backgroundColor: BRAND,
              color: `var(--zv-on-brand, ${palette["White"]})`,
              "&:hover": { backgroundColor: `color-mix(in srgb, ${BRAND} 70%, black)` },
            };
          }
          const tint = { ...radius, color: BRAND, "&:hover": { backgroundColor: BRAND_A(0.04) } };
          return ownerState.variant === "outlined" ? { ...tint, borderColor: BRAND_A(0.5), "&:hover": { ...tint["&:hover"], borderColor: BRAND } } : tint;
        },
      },
    },
    MuiTabs: { styleOverrides: { indicator: ({ ownerState }) => (ownerState.indicatorColor === "secondary" ? {} : { backgroundColor: SELECTION }) } },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ ownerState }) =>
          ownerState.color && ownerState.color !== "standard"
            ? {}
            : {
              color: `var(--zv-icon, ${palette["black-a54"]})`,
              borderColor: `var(--zv-border, ${palette["black-a12"]})`,
              "&.Mui-selected": { color: `var(--zv-text-primary, ${palette["black-a87"]})`, backgroundColor: `var(--zv-highlight, ${palette["black-a8"]})` },
            },
      },
    },
    MuiRating: { styleOverrides: { iconEmpty: { color: `var(--zv-text-muted, ${palette["black-a26"]})` } } },
    MuiCircularProgress: { styleOverrides: { colorPrimary: { color: BRAND } } },
    MuiLinearProgress: { styleOverrides: { barColorPrimary: { backgroundColor: BRAND } } },
    MuiSelect: { styleOverrides: { icon: { color: `var(--zv-icon, ${palette["black-a54"]})` } } },
    MuiDivider: { styleOverrides: { root: { borderColor: `var(--zv-border, ${palette["black-a12"]})` } } },
    MuiSkeleton: { styleOverrides: { root: { backgroundColor: `var(--zv-well, ${palette["black-a11"]})` } } },
    MuiBackdrop: {
      styleOverrides: {
        root: ({ ownerState }) => (ownerState.invisible ? {} : { backgroundColor: `var(--zv-overlay, ${palette["black-a50"]})` }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) =>
          ownerState.color && ownerState.color !== "default"
            ? {}
            : ownerState.variant === "outlined"
              ? { color: `var(--zv-text-primary, ${palette["black-a87"]})`, borderColor: `var(--zv-border, ${palette["gray-74"]})` }
              : { color: `var(--zv-text-primary, ${palette["black-a87"]})`, backgroundColor: `var(--zv-control, ${palette["black-a8"]})` },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ ownerState }) =>
          ownerState.textColor && ownerState.textColor !== "primary"
            ? {}
            : { color: `var(--zv-text-secondary, ${palette["black-a60"]})`, "&.Mui-selected": { color: SELECTION } },
      },
    },
  },
  palette: {
    primary: {
      main: mui.primary,
      contrastText: mui.primaryContrast,
    },
    secondary: {
      main: mui.secondary,
      contrastText: mui.secondaryContrast,
    },
  },
};

export const muiTheme = createTheme(muiThemeOptions);
